import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ReactDOM from 'react-dom';
import * as R from 'ramda';
import classNames from 'classnames';
import { Skeleton, Typography, withStyles } from '@branchjs/neo-components';
import type { CustomPartnerLimitResponse } from '../../../../../shared/types/customPartnerLimits.types';
import { ImageService } from '../../../main/services/ImageService/ImageService';
import PartnerCard from './PartnerCard';
import {
  TIKTOK_SAN_MACHINE_NAME,
  HUAWEI_MACHINE_NAME,
  GOOGLE_ANALYTICS_V4_WEB_MACHINE_NAME,
  GOOGLE_ANALYTICS_V4_FIREBASE_MACHINE_NAME,
  NBC_UNIVERSAL_MACHINE_NAME,
  LG_SAN_MACHINE_NAME,
  VIZIO_MACHINE_NAME,
} from '../../../../../shared/constants/adPartnerConstants';
import {
  ENABLE_HUAWEI_AD_PARTNER,
  ENABLE_TIKTOK_SAN,
  GOOGLE_ANALYTICS_V4,
  SUPPORT_NBCU_AD_PARTNER,
  SUPPORT_LG_AD_PARTNER,
} from '../../../../../shared/constants/flipperFlags';
import { selectCustomPartnerLimits, isAdPartnerVisible } from '../../../redux/domains/AdPartner/AdPartner.selectors';
import type { AdPartner } from '../../../main/types/adPartnerTypes';
import { isFeatureEnabled } from '../../../redux/domains/FlipperUpdates/Flipper.selectors';
import type { ClassNameMap } from '@mui/styles';
import type { RootState } from 'client/js/redux';

const styles = (theme) =>
  ({
    emptyListWrapper: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    emptyListImage: {
      marginBottom: theme.spacing(2),
    },
    emptyListText: {
      marginBottom: theme.spacing(1),
      textAlign: 'center',
    },
  }) as any;

export type Props = {
  classes: ClassNameMap<'emptyListWrapper' | 'emptyListImage' | 'emptyListText'>;
  scrollTop?: boolean;
  showAllPartners?: boolean;
  data: Array<AdPartner>;
  enabledText?: string;
  disabledText?: string;
  selectedPartner?: AdPartner | null;
  accessResources?: Array<string>;
  permission?: number;
  onClick: (partner: AdPartner) => void;
  onDisableClick?: (partner: AdPartner) => void;
  loading?: boolean | null;
  tikTokSANEnabled: boolean;
  huaweiEnabled: boolean;
  googleAnalyticsV4Enabled: boolean;
  customPartnerLimits: CustomPartnerLimitResponse | null;
  nbcuEnabled: boolean;
  lgEnabled: boolean;
};

export class PartnerList extends PureComponent<Props> {
  static defaultProps = {
    data: [],
    scrollTop: false,
    showAllPartners: false,
    enabledText: 'ENABLED PARTNERS',
    disabledText: 'DISABLED PARTNERS',
  };

  componentDidUpdate() {
    if (this.props.scrollTop) {
      // @ts-ignore
      ReactDOM.findDOMNode(this).scrollTop = 0;
    }
  }

  isAdPartnerAvailable = (adPartnerMachineName: string): boolean => {
    const { tikTokSANEnabled, huaweiEnabled, googleAnalyticsV4Enabled, nbcuEnabled, lgEnabled } = this.props;

    switch (adPartnerMachineName) {
      case TIKTOK_SAN_MACHINE_NAME:
        return tikTokSANEnabled;

      case HUAWEI_MACHINE_NAME:
        return huaweiEnabled;

      case GOOGLE_ANALYTICS_V4_WEB_MACHINE_NAME:
      case GOOGLE_ANALYTICS_V4_FIREBASE_MACHINE_NAME:
        return googleAnalyticsV4Enabled;
      case NBC_UNIVERSAL_MACHINE_NAME:
        return nbcuEnabled;
      case LG_SAN_MACHINE_NAME:
        return lgEnabled;
      // TO DO - remove this case when Vizio will be supported on Dashboard
      case VIZIO_MACHINE_NAME:
        return false;

      default:
        return true;
    }
  };

  getPartnersToDisplay = (partners: AdPartner[], enabled: boolean) => {
    const activePartners = partners.filter((partner) => {
      return this.props.showAllPartners || isAdPartnerVisible(partner);
    });
    return activePartners.filter((partner: AdPartner) => {
      const ingestionEnabled = R.path(['ingestion', 'enabled'], partner);

      if (ingestionEnabled) {
        return ingestionEnabled === enabled;
      }

      return !!partner.enabled === enabled && this.isAdPartnerAvailable(partner.machine_name);
    });
  };

  renderCards = (partners: AdPartner[]) => {
    return partners.map((partner: AdPartner, i: number) => {
      const isCustom = this.props.customPartnerLimits?.customPartners?.some(
        (customPartner) => customPartner.machineName === partner.machine_name
      );
      const isOrgLevel = this.props.customPartnerLimits?.customPartners?.some(
        (customPartner) => customPartner.machineName === partner.machine_name && customPartner.isOrgLevel
      );

      return (
        <PartnerCard
          accessResources={this.props.accessResources}
          permission={this.props.permission}
          onClick={this.props.onClick.bind(null, partner)}
          partner={partner}
          selectedPartner={this.props.selectedPartner}
          key={partner.machine_name}
          index={i}
          isCustom={isCustom}
          isOrgLevel={isOrgLevel}
        />
      );
    });
  };

  renderLoadingCards = () => {
    return (
      <>
        <Skeleton className="partner-list__loading-text-placeholder" variant="text" height={16} />
        {new Array(11).fill(0).map((_, index) => {
          return (
            <Skeleton
              className={classNames('default-skeleton', 'partner-list__loading-placeholder')}
              key={index}
              variant="rectangular"
              height={50}
            />
          );
        })}
      </>
    );
  };

  renderPartners = () => {
    const { data, classes, enabledText, disabledText } = this.props;
    const enabledPartners = this.getPartnersToDisplay(data, true);
    const disabledPartners = this.getPartnersToDisplay(data, false);
    const allPartners = [...enabledPartners, ...disabledPartners];

    if (!allPartners.length) {
      return (
        <div className={classes.emptyListWrapper}>
          <img
            alt="Search image"
            className={classes.emptyListImage}
            src={ImageService.getImageUrl('/static/images/ad_partner_manager/search.svg')}
          />
          <Typography className={classes.emptyListText} variant="h5">
            There are no results for your inquiry.
          </Typography>
          <Typography className={classes.emptyListText} variant="body3">
            Please edit your search and try again.
          </Typography>
        </div>
      );
    }

    return (
      <>
        {!!enabledPartners.length && (
          <>
            <div className="partner-list__header">
              <span className="partner-list__header-text">{enabledText}</span>
            </div>
            <div className="h_mb-40" data-testid="enabled-cards">
              {this.renderCards(enabledPartners) || <div className="h_p-20" key={1} />}
            </div>
          </>
        )}
        {!!disabledPartners.length && (
          <>
            <div className="partner-list__header">
              <span className="partner-list__header-text">{disabledText}</span>
            </div>
            <div data-testid="disabled-cards">{this.renderCards(disabledPartners)}</div>
          </>
        )}
      </>
    );
  };

  render() {
    const { loading } = this.props;

    return (
      <>
        <div className="partner-list">{loading ? this.renderLoadingCards() : this.renderPartners()}</div>
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  nbcuEnabled: isFeatureEnabled(state, SUPPORT_NBCU_AD_PARTNER),
  lgEnabled: isFeatureEnabled(state, SUPPORT_LG_AD_PARTNER),
  tikTokSANEnabled: isFeatureEnabled(state, ENABLE_TIKTOK_SAN),
  huaweiEnabled: isFeatureEnabled(state, ENABLE_HUAWEI_AD_PARTNER),
  googleAnalyticsV4Enabled: isFeatureEnabled(state, GOOGLE_ANALYTICS_V4),
  customPartnerLimits: selectCustomPartnerLimits(state),
});

const ConnectedPartnerList = withStyles(styles)(connect(mapStateToProps, null)(PartnerList));
export default ConnectedPartnerList;
